import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import WhatsAppForm from '../components/WhatsAppForm';
import {
  MapPin,
  MessageCircle,
  Mail,
  Clock,
  PhoneCall,
  Linkedin,
  ExternalLink,
  CheckCircle,
} from 'lucide-react';
import { images, contactInfo, socialLinks, contactByLanguage } from '../data/mock';

// Google Colors
const googleColors = {
  blue: '#4285F4',
  red: '#EA4335',
  yellow: '#FBBC04',
  green: '#34A853',
};

const ContactPage = () => {
  const { language, t } = useLanguage();

  const currentContact = contactByLanguage[language] || contactByLanguage.es;

  const openWhatsApp = () => {
    window.open(`https://wa.me/${currentContact.whatsapp}`, '_blank');
  };

  const callPhone = () => {
    window.location.href = `tel:${currentContact.phone.replace(/\s/g, '')}`;
  };

  const content = {
    es: {
      hero: {
        title: 'Contacto',
        subtitle: 'Estamos listos para ayudarte a hacer crecer tu negocio con Google Ads',
      },
      info: {
        title: 'Información de Contacto',
        response: 'Respuesta en menos de 24h',
        whatsapp: 'WhatsApp directo',
        email: 'Email',
        location: 'Ubicación',
        linkedin: 'LinkedIn',
        hours: 'Horario',
        hoursValue: 'Lun - Vie: 9:00 - 18:00',
      },
      features: [
        'Auditoría gratuita sin compromiso',
        'Respuesta en menos de 24 horas',
        'Consulta inicial por teléfono o video',
        'Presupuesto personalizado',
      ],
    },
    fr: {
      hero: {
        title: 'Contact',
        subtitle: 'Nous sommes prêts à vous aider à développer votre entreprise avec Google Ads',
      },
      info: {
        title: 'Informations de Contact',
        response: 'Réponse en moins de 24h',
        whatsapp: 'WhatsApp direct',
        email: 'Email',
        location: 'Localisation',
        linkedin: 'LinkedIn',
        hours: 'Horaires',
        hoursValue: 'Lun - Ven: 9:00 - 18:00',
      },
      features: [
        'Audit gratuit sans engagement',
        'Réponse en moins de 24 heures',
        'Consultation initiale par téléphone ou vidéo',
        'Devis personnalisé',
      ],
    },
    en: {
      hero: {
        title: 'Contact',
        subtitle: "We're ready to help you grow your business with Google Ads",
      },
      info: {
        title: 'Contact Information',
        response: 'Response in less than 24h',
        whatsapp: 'Direct WhatsApp',
        email: 'Email',
        location: 'Location',
        linkedin: 'LinkedIn',
        hours: 'Hours',
        hoursValue: 'Mon - Fri: 9:00 - 18:00',
      },
      features: [
        'Free audit with no commitment',
        'Response in less than 24 hours',
        'Initial consultation by phone or video',
        'Personalized quote',
      ],
    },
  };

  const c = content[language] || content.es;

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-white shadow-md px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <img src={images.googleAdsLogo} alt="Google Ads" className="h-5 w-auto" />
              <span className="text-slate-700">{c.hero.title}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
              {c.hero.title}
            </h1>
            <p className="text-xl text-slate-600">
              {c.hero.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6">
            {c.features.map((feature, index) => {
              const colors = [googleColors.blue, googleColors.green, googleColors.red, googleColors.yellow];
              return (
                <div key={index} className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5" style={{ color: colors[index] }} />
                  <span className="text-slate-700 font-medium">{feature}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-slate-900 mb-8">{c.info.title}</h2>
              
              <div className="space-y-4">
                {/* Phone */}
                <button
                  onClick={callPhone}
                  className="w-full flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow text-left"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: googleColors.green }}>
                    <PhoneCall className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{c.info.response}</h3>
                    <span className="text-green-600 font-medium">{currentContact.phone}</span>
                  </div>
                </button>

                {/* WhatsApp */}
                <button
                  onClick={openWhatsApp}
                  className="w-full flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow text-left"
                >
                  <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                    <MessageCircle className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{c.info.whatsapp}</h3>
                    <span className="text-emerald-600 font-medium">{currentContact.phone}</span>
                  </div>
                </button>

                {/* Email */}
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: googleColors.blue }}>
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{c.info.email}</h3>
                    <span className="text-blue-600 font-medium">{contactInfo.email}</span>
                  </div>
                </a>

                {/* Location */}
                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: googleColors.red }}>
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{c.info.location}</h3>
                    <span className="text-slate-600">{currentContact.location}</span>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: googleColors.yellow }}>
                    <Clock className="w-6 h-6 text-slate-800" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{c.info.hours}</h3>
                    <span className="text-slate-600">{c.info.hoursValue}</span>
                  </div>
                </div>

                {/* LinkedIn */}
                <a
                  href={socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start space-x-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 bg-[#0A66C2] rounded-xl flex items-center justify-center text-white flex-shrink-0">
                    <Linkedin className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">{c.info.linkedin}</h3>
                    <span className="text-[#0A66C2] font-medium flex items-center">
                      Ulysse Berneau
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </span>
                  </div>
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              <Card className="bg-white shadow-2xl border-0 rounded-2xl overflow-hidden">
                <div className="h-2 flex">
                  <div className="flex-1" style={{ backgroundColor: googleColors.blue }} />
                  <div className="flex-1" style={{ backgroundColor: googleColors.red }} />
                  <div className="flex-1" style={{ backgroundColor: googleColors.yellow }} />
                  <div className="flex-1" style={{ backgroundColor: googleColors.green }} />
                </div>
                <CardContent className="p-8 lg:p-10">
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{t.form.title}</h3>
                  <p className="text-slate-500 mb-8">{t.form.subtitle}</p>
                  <WhatsAppForm />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
